import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import type { MentionUser } from '../../types'
import styles from './mention-list.module.css'

export type MentionListProps = {
  items: MentionUser[]
  loading?: boolean
  command: (item: { id: number | string; label: string }) => void
}

export type MentionListHandle = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  ({ items, loading = false, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

    useEffect(() => {
      setSelectedIndex(0)
    }, [items])

    useEffect(() => {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' })
    }, [selectedIndex])

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index]
        if (!item) return
        command({ id: item.id, label: item.label })
      },
      [items, command],
    )

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
          if (items.length === 0) return false

          if (event.key === 'ArrowUp') {
            setSelectedIndex(prev => (prev <= 0 ? items.length - 1 : prev - 1))
            return true
          }

          if (event.key === 'ArrowDown') {
            setSelectedIndex(prev => (prev >= items.length - 1 ? 0 : prev + 1))
            return true
          }

          if (event.key === 'Enter') {
            selectItem(selectedIndex)
            return true
          }

          return false
        },
      }),
      [selectedIndex, selectItem, items.length],
    )

    if (loading) {
      return (
        <div className={styles.mentionList}>
          <div className={styles.empty}>Loading…</div>
        </div>
      )
    }

    if (items.length === 0) {
      return (
        <div className={styles.mentionList}>
          <div className={styles.empty}>No users found</div>
        </div>
      )
    }

    return (
      <div className={styles.mentionList}>
        {items.map((item, index) => (
          <button
            key={item.id}
            ref={el => {
              itemRefs.current[index] = el
            }}
            className={`${styles.item} ${
              index === selectedIndex ? styles.itemSelected : ''
            }`}
            onClick={() => selectItem(index)}
            type="button"
          >
            <span className={styles.name}>{item.display ?? item.label}</span>
            {item.description && (
              <span className={styles.email}>{item.description}</span>
            )}
          </button>
        ))}
      </div>
    )
  },
)

MentionList.displayName = 'MentionList'
